import {ComponentStore} from "@ngrx/component-store";
import {Observable, withLatestFrom, combineLatest} from "rxjs";
import {AppState} from "../../../shared/store/store";
import {
  selectDataSourceProviders,
  selectDataSourceProvidersLoaded
} from "../../../shared/store/dataSourceProviders/dataSourceProviders.selectors";
import {selectDataSourcesAll, selectDataSourcesAllLoaded} from "../../../shared/store/dataSourcesAll/dataSourcesAll.selectors";
import {Store} from "@ngrx/store";
import {Injectable} from "@angular/core";
import {filter, tap} from "rxjs/operators";
import {DataSourceProvider} from "../../../shared/store/dataSourceProviders/dataSourceProviders";
import {Serializer, Serializers} from "../../../shared/interfaces/serializer";
import {DataSource} from "../../../shared/interfaces/data-source";
import {Sort} from "../../../shared/libraries/sort";

export interface AddDataSetUsageStoreState {
  multipleList: Serializer<DataSourceProvider>[],
  topList: Serializer<DataSourceProvider | DataSource>[],
  allList: Serializer<DataSourceProvider | DataSource>[],
  notFoundList: Serializer<DataSourceProvider | DataSource>[],
  groupedList: object[]
}

@Injectable()
export class AddDataSetUsageStore extends ComponentStore<AddDataSetUsageStoreState> {
  public readonly multipleList$: Observable<Serializer<DataSourceProvider>[]> = this.select((store: AddDataSetUsageStoreState): Serializer<DataSourceProvider>[] => store.multipleList);
  public readonly topList$: Observable<Serializer<DataSourceProvider | DataSource>[]> = this.select((store: AddDataSetUsageStoreState): Serializer<DataSourceProvider | DataSource>[] => store.topList);
  public readonly allList$: Observable<Serializer<DataSourceProvider | DataSource>[]> = this.select((store: AddDataSetUsageStoreState): Serializer<DataSourceProvider | DataSource>[] => store.allList);
  public readonly notFoundList$: Observable<Serializer<DataSourceProvider | DataSource>[]> = this.select((store: AddDataSetUsageStoreState): Serializer<DataSourceProvider | DataSource>[] => store.notFoundList);
  public readonly groupedList$: Observable<object[]> = this.select((store: AddDataSetUsageStoreState): object[] => store.groupedList);


  private readonly _init$ = this.effect((): Observable<[[boolean, boolean], Serializer<DataSourceProvider>[], Serializer<DataSource>[]]> => combineLatest([
    this._store.select(selectDataSourceProvidersLoaded),
    this._store.select(selectDataSourcesAllLoaded)
  ]).pipe(
    filter(([providers, dataSources]: [boolean, boolean]): boolean => providers && dataSources),
    withLatestFrom(
      this._store.select(selectDataSourceProviders),
      this._store.select(selectDataSourcesAll)
    ),
    tap(([_, providers, dataSources]: [[boolean, boolean], Serializer<DataSourceProvider>[], Serializer<DataSource>[]]): void => {
      const meta: Serializer<DataSourceProvider> = providers.find((provider: Serializer<DataSourceProvider>) => provider.attributes.slug === 'meta');
      const facebookAds: Serializer<DataSource> = dataSources.find((provider: Serializer<DataSource>) => provider.attributes.slug === 'facebook-ads');
      const all: Serializer<DataSource | DataSourceProvider>[] = dataSources.map((dataSource: Serializer<DataSource>): Serializer<DataSource | DataSourceProvider> => dataSource === facebookAds && meta || dataSource);

      (meta.attributes as DataSourceProvider & any).starred = true;
      (meta.attributes as DataSourceProvider & any).is_installable = true;
      meta.relationships.data_source_group = facebookAds.relationships.data_source_group;

      this.patchState({
        multipleList: providers.filter((provider: Serializer<DataSourceProvider>) => provider !== meta),
        topList: all.filter((dataSource: Serializer<DataSource>): boolean => dataSource.attributes.starred),
        allList: all,
        notFoundList: all.filter((dataSource: Serializer<DataSource>): boolean => ['custom-advertising'].includes(dataSource.attributes.slug)),
        groupedList: this.buildGroups(all as Serializer<DataSource>[])
      });
    })
  ));

  constructor(
    private readonly _store: Store<AppState>
  ) {
    super({
      multipleList: [],
      topList: [],
      allList: [],
      notFoundList: [],
      groupedList: []
    });
  }

  private buildGroups(dataSources: Serializers<DataSource>): { name: string, showGroup: number, dataSources: Serializer<DataSource>[] }[] {
    const dataSourcesGroups: object = {};
    const showGroups: { [key: string]: number } = {};
    const output: { name: string, showGroup: number, dataSources: Serializer<DataSource>[] }[] = [];

    dataSources.forEach(dataSource => {
      const dataSourceGroup = dataSource.relationships.data_source_group.data.attributes;

      if (dataSourcesGroups[dataSourceGroup.name] === undefined) {
        dataSourcesGroups[dataSourceGroup.name] = [];
        showGroups[dataSourceGroup.name] = 0;
      }

      if (dataSource.attributes.is_installable) {
        showGroups[dataSourceGroup.name]++;
      }

      dataSourcesGroups[dataSourceGroup.name].push(dataSource);
    });

    for (const key in dataSourcesGroups) {
      output.push({
        showGroup: showGroups[key],
        name: key,
        dataSources: dataSourcesGroups[key]
      });
    }

    return output.sort((a, b) => Sort.alphaAsc(a.name, b.name));
  }
}
